/* Canvello v8 — home hero chapters (Task 3.1). sections/cv-hero.liquid.
 *
 * v8 (L1407, L1912-1916) turns the hero to the next chapter every 7.6s and
 * lets a thumbnail pick one. Here:
 *  - A chapter swaps the kicker, the split headline (CVMotion.split), the
 *    lede, the plate (the new one fades in over the old, which stays shown
 *    beneath as data-prev until covered), the ticks, caption and counter.
 *  - Autoplay only with data-autoplay, motion allowed (not reduced, not
 *    Motion 0) and outside the theme editor. It holds while the pointer is
 *    on the chapters or the ticks, while keyboard focus is in the hero, while
 *    the hero is off screen and while the tab is hidden, and resumes with the
 *    time that was left, so the current chapter's progress line (CSS, paused
 *    by data-held) and the clock agree. Picking a chapter restarts the cycle
 *    from it. The ticks are a pause/play button (WCAG 2.2.2).
 *  - The text block is a polite live region only for a visitor's choice, so
 *    autoplay never talks over a screen reader.
 *  - Plates 2-n are rendered hidden, so their lazy images wait; they are
 *    shown (still transparent) once the page has loaded.
 *  - Theme editor: selecting a chapter block shows it.
 */
(() => {
  if (customElements.get('cv-hero')) return;

  const STEP = 7600;
  // The plate crossfade (--cv-t-unveil) plus a margin: then the old plate
  // beneath can go.
  const SETTLE = 1700;

  class CVHero extends HTMLElement {
    connectedCallback() {
      this.chapters = Array.from(this.querySelectorAll('.cv-hero__chapter'));
      this.imgs = Array.from(this.querySelectorAll('[data-cv-hero-img]'));
      if (this.chapters.length < 2) return;

      this.index = 0;
      this.left = STEP;
      this.ticking = false;
      this.text = this.querySelector('.cv-hero__text');
      this.headline = this.querySelector('[data-cv-hero-title]');
      this.toggle = this.querySelector('[data-cv-hero-toggle]');
      this.still = this.querySelector('.cv-hero__ticks--still');
      this.held = { hover: false, focus: false, hidden: document.hidden, away: false, user: false };
      this.style.setProperty('--cv-hero-step', STEP + 'ms');

      const motion = window.CVMotion ? window.CVMotion.motion : 1;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const editor = window.Shopify && window.Shopify.designMode;
      this.auto = this.hasAttribute('data-autoplay') && motion > 0 && !reduced && !editor;
      if (!this.auto) {
        if (this.toggle) this.toggle.hidden = true;
        if (this.still) this.still.hidden = false;
      }

      this.chapters.forEach((btn, i) =>
        btn.addEventListener('click', () => {
          this.text.setAttribute('aria-live', 'polite');
          this.show(i);
          this.schedule();
        })
      );

      if (this.toggle) {
        this.toggle.addEventListener('click', () => {
          this.held.user = !this.held.user;
          this.paint();
          this.schedule();
        });
      }

      // Hover holds only where a visitor is about to choose: the hero fills
      // the first screen, so a hold on all of it would rarely let it turn.
      const strip = this.querySelector('.cv-hero__chapters');
      [strip, this.toggle].forEach((el) => {
        if (!el) return;
        el.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.hold('hover', true));
        el.addEventListener('pointerleave', () => this.hold('hover', false));
      });
      // Keyboard focus only: a click focuses a chapter button too, and that
      // must not hold the cycle it has just restarted.
      this.addEventListener('focusin', (e) => this.hold('focus', e.target.matches(':focus-visible')));
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.hold('focus', false);
      });
      this.onVisible = () => this.hold('hidden', document.hidden);
      document.addEventListener('visibilitychange', this.onVisible);
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([entry]) => this.hold('away', !entry.isIntersecting), { threshold: 0.15 });
        this.io.observe(this);
      }

      this.onBlock = (e) => {
        const i = this.chapters.findIndex((b) => b.contains(e.target) || e.target.contains(b));
        if (i > -1) this.show(i);
      };
      document.addEventListener('shopify:block:select', this.onBlock);

      const wake = () => {
        this.imgs.forEach((img) => (img.hidden = false));
        this.toggleAttribute('data-auto', this.auto);
        this.paint();
        this.schedule();
      };
      if (document.readyState === 'complete') wake();
      else window.addEventListener('load', wake, { once: true });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.settle);
      if (this.io) this.io.disconnect();
      document.removeEventListener('visibilitychange', this.onVisible);
      document.removeEventListener('shopify:block:select', this.onBlock);
    }

    hold(why, on) {
      if (this.held[why] === on) return;
      this.held[why] = on;
      this.paint();
      this.schedule();
    }

    get running() {
      const h = this.held;
      return this.auto && !h.hover && !h.focus && !h.hidden && !h.away && !h.user;
    }

    // data-held pauses the progress line; the ticks button's label and
    // cursor word follow the visitor's pause.
    paint() {
      this.toggleAttribute('data-held', this.auto && !this.running);
      this.toggleAttribute('data-paused', this.auto && this.held.user);
      if (!this.toggle) return;
      const paused = this.held.user;
      this.toggle.setAttribute('aria-label', paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.toggle.setAttribute('data-cur', paused ? this.dataset.playWord : this.dataset.pauseWord);
    }

    // Stops the clock, keeping the time left on the current chapter.
    stop() {
      clearTimeout(this.timer);
      if (this.ticking) this.left -= performance.now() - this.started;
      this.ticking = false;
    }

    schedule() {
      this.stop();
      if (!this.running || !this.hasAttribute('data-auto')) return;
      this.started = performance.now();
      this.ticking = true;
      this.timer = setTimeout(() => {
        this.ticking = false;
        this.text.removeAttribute('aria-live');
        this.show((this.index + 1) % this.chapters.length);
        this.schedule();
      }, Math.max(0, this.left));
    }

    show(i) {
      if (i === this.index) return;
      const btn = this.chapters[i];
      const d = btn.dataset;
      const from = this.index;
      this.index = i;
      clearTimeout(this.timer);
      this.ticking = false;
      this.left = STEP;

      this.chapters.forEach((b, j) => b.setAttribute('aria-pressed', String(j === i)));
      this.imgs.forEach((img, j) => {
        img.hidden = false;
        img.toggleAttribute('data-on', j === i);
        img.toggleAttribute('data-prev', j === from);
      });
      clearTimeout(this.settle);
      this.settle = setTimeout(() => this.imgs.forEach((img) => img.removeAttribute('data-prev')), SETTLE);
      this.querySelectorAll('.cv-hero__ticks').forEach((ticks) =>
        Array.from(ticks.children).forEach((t, j) => t.toggleAttribute('data-on', j === i))
      );
      const set = (sel, text) => {
        const el = this.querySelector(sel);
        if (el && text != null) el.textContent = text;
      };
      set('[data-cv-hero-plate]', d.caption);
      set('[data-cv-hero-n]', String(i + 1).padStart(2, '0'));

      // Kicker and lede fade out and back with the new text; the headline
      // rises word by word again.
      this.setAttribute('data-swapping', '');
      clearTimeout(this.swap);
      this.swap = setTimeout(() => {
        set('[data-cv-hero-kicker]', d.kicker);
        set('[data-cv-hero-copy]', d.copy);
        this.removeAttribute('data-swapping');
      }, 250);
      if (this.headline) {
        if (window.CVMotion) window.CVMotion.split(this.headline, d.title);
        else this.headline.textContent = d.title;
      }
    }
  }

  customElements.define('cv-hero', CVHero);
})();
